import React, { useEffect, useState } from "react";
import ReactDOM from "react-dom/client";
import { BrowserRouter as Router, Routes, Route, Navigate } from "react-router-dom";
import { isAdmin, isNormalUser, userInfo, isConsultant, appInfo } from "./authConfig";
import "./index.css";
import "./i18n/i18n";
import Layout from "./pages/layout/Layout";
import Chat from "./pages/chat/Chat";
import Feedback from "./pages/chat/Feedbacks";
import Report from "./pages/report/Report";
import Settings from "./pages/Settings/Settings";
import Updates from "./pages/updates/Update";
import TermsAndConditions from "./pages/Settings/TermsAndConditions";
import GuideLines from "./pages/Settings/GuideLines";
import Terms from "./pages/Settings/Terms";
import { initializeIcons } from "@fluentui/react/lib/Icons";
import store from "./Store";
import { Provider } from "react-redux";
import styled, { ThemeProvider } from "styled-components";
import { isValidAppType } from "./api";
import MasterTable from "./pages/Settings/MasterTable";
import ProductInstructions from "./pages/Settings/ProductInstructions";
import CustomerPtfVersions from "./pages/Settings/CustomerPtfVersions";
import { Dialog, DialogTitle, DialogActions, DialogContent, Button } from "@mui/material";
import i18next from "./i18n/i18n";

// Augment styled-components DefaultTheme to include our theme shape
declare module "styled-components" {
    export interface DefaultTheme {
        fontFamily: string;
        fontColor: string;
    }
}
import type { DefaultTheme } from "styled-components";

initializeIcons(undefined, { disableWarnings: true });

const theme: DefaultTheme = {
    fontFamily: `"BIZ UDPGothic", -apple-system, BlinkMacSystemFont, sans-serif !important`,
    fontColor: "#5A5A5A !important"
};

const AppThemeContainer = styled.div`
    font-family: ${props => props.theme.fontFamily};
    color: ${props => props.theme.fontColor};
`;

const ProtectedRoute = ({ children, condition }: { children: React.ReactElement; condition: boolean }) => {
    const [open, setOpen] = useState(!condition);
    const [redirect, setRedirect] = useState(false);

    const handleClose = () => {
        setOpen(false);
        setRedirect(true);
    };

    if (redirect) return <Navigate to="/" replace />;

    return (
        <>
            {!condition && (
                <Dialog open={open} onClose={handleClose}>
                    <DialogTitle>{i18next.t("homepage.accessDenied")}</DialogTitle>
                    <DialogContent>{i18next.t("homepage.notAuthorizedMessage")}</DialogContent>
                    <DialogActions>
                        <Button onClick={handleClose} autoFocus>
                            {i18next.t("homepage.onOK")}
                        </Button>
                    </DialogActions>
                </Dialog>
            )}
            {condition && children}
        </>
    );
};

function App() {
    const [option, setOption] = useState<boolean>(false);
    const [startTourByIcon, setStartTourByIcon] = useState<boolean>(false);

    useEffect(() => {
        const appTypeValue = appInfo.use_all_tab === true ? userInfo?.apptype : userInfo?.apptype !== "ALL" ? userInfo?.apptype : "FAQ";
        const normalizedAppType =
            appTypeValue === "ALL" && appInfo?.use_all_tab !== true
                ? "FAQ"
                : isValidAppType(appTypeValue)
                  ? appTypeValue
                  : appInfo.use_all_tab === true
                    ? "ALL"
                    : "FAQ";
        sessionStorage.setItem("SelectedAppType", normalizedAppType);
    }, []);

    const onChatClick = () => setOption(true);
    const startTour = () => setStartTourByIcon(true);
    const stopTour = () => setStartTourByIcon(false);
    const onChatClear = () => setOption(false);

    const [userDetail, setUserDetail] = useState({
        terms_accepted: userInfo.terms_accepted
    });

    const onAcceptTerms = () => {
        setUserDetail({ ...userInfo, terms_accepted: "accepted" });
    };

    return (
        <Router>
            <div>
                <Routes>
                    {userDetail.terms_accepted !== "accepted" ? (
                        <>
                            <Route path="/" element={<TermsAndConditions onAccept={onAcceptTerms} />} />
                            <Route path="/assistant/terms" element={<TermsAndConditions onAccept={onAcceptTerms} />} />
                            <Route path="*" element={<Navigate to="/" />} />
                        </>
                    ) : (
                        <>
                            <Route path="/" element={<Navigate to="/assistant/chat" />} />
                            <Route path="/assistant/*" element={<Layout callback={onChatClick} startTour={startTour} />}>
                                <Route
                                    path="chat"
                                    element={<Chat executeClear={option} callback={onChatClear} startTourByIcon={startTourByIcon} startTour={stopTour} />}
                                />
                                <Route
                                    path="feedbacks"
                                    element={
                                        <ProtectedRoute condition={isAdmin || !isNormalUser}>
                                            <Feedback />
                                        </ProtectedRoute>
                                    }
                                />
                                <Route
                                    path="reports"
                                    element={
                                        <ProtectedRoute condition={isAdmin || isConsultant}>
                                            <Report />
                                        </ProtectedRoute>
                                    }
                                />
                                <Route
                                    path="settings"
                                    element={
                                        <ProtectedRoute condition={isAdmin}>
                                            <Settings />
                                        </ProtectedRoute>
                                    }
                                />
                                <Route
                                    path="master-settings"
                                    element={
                                        <ProtectedRoute condition={isAdmin}>
                                            <MasterTable />
                                        </ProtectedRoute>
                                    }
                                />
                                <Route
                                    path="product-instructions"
                                    element={
                                        <ProtectedRoute condition={isAdmin}>
                                            <ProductInstructions />
                                        </ProtectedRoute>
                                    }
                                />
                                <Route
                                    path="customer-ptf-versions"
                                    element={
                                        <ProtectedRoute condition={isAdmin}>
                                            <CustomerPtfVersions />
                                        </ProtectedRoute>
                                    }
                                />
                                <Route path="*" element={<Navigate to="/assistant/chat" />} />
                            </Route>
                            <Route path="/assistant/updates" element={<Updates />} />
                            <Route path="/assistant/terms" element={<Terms />} />
                            <Route path="/assistant/guide" element={<GuideLines />} />
                            <Route path="/*" element={<Navigate to="/assistant/chat" />} />
                        </>
                    )}
                </Routes>
            </div>
        </Router>
    );
}

ReactDOM.createRoot(document.getElementById("root") as HTMLElement).render(
    <React.StrictMode>
        <Provider store={store}>
            <ThemeProvider theme={theme}>
                <AppThemeContainer>
                    <App />
                </AppThemeContainer>
            </ThemeProvider>
        </Provider>
    </React.StrictMode>
);
